/**
 * 后台蓝宝AI 浮动助手：右下角 FAB + 右侧抽屉对话窗（2026-10-15）
 * - 对接 /api/ai/chat，与「蓝宝AI 问答」页同一后端链路（IntentRouter/RAG/翻译短路）
 * - 翻译用法：直接发送「翻译成英文：<原文>」，后端短路直答译文，不走知识库
 * - 助手消息一键复制：方便粘贴到分类英文名 / SPU 英文简介等英文表单字段
 * - 会话 ID 持久化 localStorage（与问答页会话互相独立；重开抽屉自动回显历史）
 * - 悬浮球可拖动（位置记忆 localStorage）：避免挡住业务页面（如抽屉/弹窗）右下角按钮
 */
import { Box, Drawer, Fab, Typography, TextField, IconButton, Snackbar, Alert, Tooltip, Avatar } from '@mui/material';
import { useState, useRef, useEffect, useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import { iotAiApi } from '@/api/iot';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  createdAt: string;
}

const SESSION_KEY = 'admin-lanbao-session';
const FAB_POS_KEY = 'admin-lanbao-fab-pos';

function newSessionId(): string {
  const id = 'admin-lanbao-' + Date.now();
  localStorage.setItem(SESSION_KEY, id);
  return id;
}

export default function LanBaoAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState<string>(() => localStorage.getItem(SESSION_KEY) || newSessionId());
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const endRef = useRef<HTMLDivElement>(null);

  // 悬浮球位置（px，相对视口左上角）：可拖动，位置持久化；null = 默认右下角
  const [fabPos, setFabPos] = useState<{ x: number; y: number } | null>(() => {
    try {
      const raw = localStorage.getItem(FAB_POS_KEY);
      const p = raw ? JSON.parse(raw) : null;
      return p && Number.isFinite(p.x) && Number.isFinite(p.y) ? p : null;
    } catch {
      return null;
    }
  });
  const dragRef = useRef<{ startX: number; startY: number; origX: number; origY: number; w: number; h: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const posRef = useRef<{ x: number; y: number } | null>(fabPos);
  useEffect(() => { posRef.current = fabPos; }, [fabPos]);

  /** 按下开始拖动：移动超过 6px 视为拖拽（不触发点击打开），松手记忆位置 */
  const handleFabPointerDown = useCallback((e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origX: rect.left, origY: rect.top, w: rect.width, h: rect.height, moved: false };
    const onMove = (ev: PointerEvent) => {
      const st = dragRef.current;
      if (!st) return;
      const dx = ev.clientX - st.startX;
      const dy = ev.clientY - st.startY;
      if (!st.moved && Math.hypot(dx, dy) < 6) return;
      st.moved = true;
      // 视口内 8px 安全边距夹取，防止拖出屏幕
      const x = Math.min(Math.max(8, st.origX + dx), window.innerWidth - st.w - 8);
      const y = Math.min(Math.max(8, st.origY + dy), window.innerHeight - st.h - 8);
      setFabPos({ x, y });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      const st = dragRef.current;
      dragRef.current = null;
      if (st?.moved) {
        suppressClickRef.current = true; // 拖拽结束：抑制紧随其后的 click（不弹对话窗）
        try {
          if (posRef.current) localStorage.setItem(FAB_POS_KEY, JSON.stringify(posRef.current));
        } catch { /* 忽略 */ }
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, []);

  // 打开抽屉时拉取当前会话历史（失败静默：无权限/网络异常不影响继续新对话）
  useEffect(() => {
    if (!open) return;
    let alive = true;
    iotAiApi.chatHistory(sessionId)
      .then((list) => {
        if (!alive || !Array.isArray(list) || list.length === 0) return;
        setMessages(list.map((h) => ({
          id: String(h.id),
          role: h.role,
          content: h.content,
          createdAt: String(h.createdAt),
        })));
      })
      .catch(() => { /* 静默 */ });
    return () => { alive = false; };
  }, [open, sessionId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((prev) => [...prev, {
      id: 'u-' + Date.now(), role: 'user', content: text, createdAt: new Date().toISOString(),
    }]);
    setInput('');
    setSending(true);
    try {
      const res = await iotAiApi.chat({ sessionId, message: text });
      setMessages((prev) => [...prev, {
        id: res?.id || 'a-' + Date.now(),
        role: 'assistant',
        content: res?.content || '（无回答，请稍后重试）',
        createdAt: res?.createdAt || new Date().toISOString(),
      }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        id: 'err-' + Date.now(),
        role: 'assistant',
        content: `请求失败：${(err as Error)?.message || '未知错误'}`,
        createdAt: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId]);

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnack({ open: true, message: '已复制到剪贴板', severity: 'success' });
    } catch {
      setSnack({ open: true, message: '复制失败，请手动选择文本复制', severity: 'error' });
    }
  }, []);

  const startNew = useCallback(() => {
    setSessionId(newSessionId());
    setMessages([]);
  }, []);

  return (
    <>
      <Tooltip title="蓝宝AI 助手（问答 · 中英互译）" placement="left">
        <Fab
          aria-label="蓝宝AI 助手"
          onPointerDown={handleFabPointerDown}
          onClick={() => {
            // 拖拽松手后的 click 不打开对话窗
            if (suppressClickRef.current) {
              suppressClickRef.current = false;
              return;
            }
            setOpen(true);
          }}
          sx={{
            position: 'fixed',
            ...(fabPos ? { top: fabPos.y, left: fabPos.x } : { bottom: 24, right: 24 }),
            zIndex: 1450, // 高于 Dialog(1300)：业务弹窗（如新增分类）打开时浮动球仍可点
                          // 低于 Snackbar(1400) 之上无碍；低于 Tooltip(1500) 避免提示被盖
            width: 56,
            height: 56,
            bgcolor: '#FFFFFF',
            boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            cursor: 'grab',
            touchAction: 'none', // 触屏拖拽必需：阻止浏览器把手势当滚动
            '&:hover': { bgcolor: '#F5F5F5' },
            '&:active': { cursor: 'grabbing' },
          }}
        >
          <Avatar src="/logo/lanbao.jpg" alt="蓝宝AI" sx={{ width: '100%', height: '100%', bgcolor: 'transparent' }} />
        </Fab>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ zIndex: 1450 }} // 与 FAB 同层：从业务弹窗里唤起蓝宝时，抽屉盖在弹窗之上，关闭后回到弹窗
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(2px)' } } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', width: 420, height: '100vh', bgcolor: '#F5F7FA' }}>
          {/* 头部 */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.5, bgcolor: '#1565C0', color: '#FFFFFF', flexShrink: 0 }}>
            <Avatar src="/logo/lanbao.jpg" alt="蓝宝AI" sx={{ width: 28, height: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>蓝宝AI 助手</Typography>
              <Typography sx={{ fontSize: '11px', color: '#BBDEFB' }}>业务问答 · 中英互译 · 一键复制</Typography>
            </Box>
            <Tooltip title="新会话">
              <IconButton size="small" onClick={startNew} sx={{ color: '#90CAF9', '&:hover': { color: '#FFFFFF' } }}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: '#FFFFFF' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* 消息区 */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {messages.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Box component="img" src="/logo/lanbao.jpg" alt="蓝宝AI" sx={{ width: 64, height: 64, borderRadius: '50%', mb: 1.5, objectFit: 'cover' }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#424245', mb: 0.5 }}>蓝宝AI 随时为您服务</Typography>
                <Typography sx={{ fontSize: '12px', color: '#9AA0A6', lineHeight: 1.8 }}>
                  问我业务问题，或直接发送：<br />
                  翻译成英文：新鲜大姜 4.5 美元/公斤
                </Typography>
              </Box>
            )}
            {messages.map((m) => (
              <Box key={m.id} sx={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <Box
                  sx={{
                    maxWidth: '85%',
                    px: 1.5,
                    py: 1.25,
                    borderRadius: '14px',
                    fontSize: '13px',
                    lineHeight: 1.65,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    bgcolor: m.role === 'user' ? '#1E88E5' : '#FFFFFF',
                    color: m.role === 'user' ? '#FFFFFF' : '#212121',
                    border: m.role === 'user' ? 'none' : '1px solid #E8EAED',
                    borderBottomRightRadius: m.role === 'user' ? '4px' : '14px',
                    borderBottomLeftRadius: m.role !== 'user' ? '4px' : '14px',
                  }}
                >
                  {m.content}
                  {m.role !== 'user' && m.content && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                      <Tooltip title="复制">
                        <IconButton size="small" onClick={() => void copy(m.content)} sx={{ color: '#9AA0A6', '&:hover': { color: '#1E88E5' } }}>
                          <ContentCopyIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            ))}
            {sending && (
              <Typography role="status" sx={{ fontSize: '11px', color: '#9AA0A6', px: 0.5 }}>蓝宝AI 正在思考…</Typography>
            )}
            <div ref={endRef} />
          </Box>

          {/* 输入区 */}
          <Box sx={{ p: 1.5, borderTop: '1px solid #E8EAED', bgcolor: '#FFFFFF', flexShrink: 0, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder="输入问题，或「翻译成英文：……」"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !(e as unknown as { nativeEvent?: { isComposing?: boolean } }).nativeEvent?.isComposing) {
                  e.preventDefault();
                  void send();
                }
              }}
              disabled={sending}
            />
            <IconButton
              aria-label="发送"
              onClick={() => void send()}
              disabled={!input.trim() || sending}
              sx={{ bgcolor: '#1E88E5', color: '#FFFFFF', '&:hover': { bgcolor: '#1565C0' }, '&.Mui-disabled': { bgcolor: '#E3F2FD', color: '#FFFFFF' } }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={snack.open}
        autoHideDuration={2000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} sx={{ width: '100%' }} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
